import React, { useEffect, useState } from "react";

import {
  Chart as ChartJS,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  TimeScale,
  Title,
  Tooltip,
} from "chart.js";
import "chartjs-adapter-luxon";
import { Scatter } from "react-chartjs-2";
import {
  clone,
  friendlyWindUnitName,
  graphHeight,
  locationLabel,
  SC_BASE_PATH,
} from "../util/util";
import { LocationData, WindowSize, GraphData } from "../datastructures";
import { strip_empty_label_plugin } from "./chartParts/Strip_empty_label_plugin";
import { optionsWindDir } from "./chartParts/OptionsWindDir";
import { optionsWindSpd } from "./chartParts/OptionsWindSpd";
import { labelCallback } from "./chartParts/LabelCallback";
import { useRemoteData } from "../util/useRemoteData";

ChartJS.register(
  TimeScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Filler,
  Legend,
);

ChartJS.defaults.font.size = 12;

export interface SpotForecastGraphProperties {
  location: LocationData;
  activityId?: number;
  unifiedUnit: string;
  windUnits?: string;
  day: string;
  duringDayOnly: boolean;
  windowSize: WindowSize;
  runsToShow: string;
  showSpotHarmonie?: boolean;
  spotHarmData?: any; //GraphData | null;
}

function mergeLocationAndSpotData(
  locationData: GraphData | null,
  spotData: GraphData | null,
): GraphData | null {
  if (!locationData) {
    return null;
  }
  // Merge datasets from both location and spot data (spotData may be null)
  const mergedDatasets = [
    ...(locationData.data.datasets || []),
    ...(spotData && spotData.data.datasets ? spotData.data.datasets : []),
  ];
  return {
    data: {
      datasets: mergedDatasets,
    },
  };
}

export const SpotForecastGraph: React.FC<SpotForecastGraphProperties> = ({
  location,
  activityId,
  unifiedUnit,
  windUnits,
  day,
  duringDayOnly,
  windowSize,
  runsToShow,
  showSpotHarmonie = true,
  spotHarmData,
}) => {
  const [graphData, setGraphData] = useState<GraphData | null>(null);

  const activityUrlString = activityId ? "&activity_id=" + activityId : "";

  const duringDayOnlyString = duringDayOnly
    ? "&duringDayOnly=" + duringDayOnly
    : "";

  const locationLbl = locationLabel(location);
  const locationId = location.id;

  // Location data URL and dependencies
  const runsString = runsToShow ? "&runsToShow=" + runsToShow : "";
  const locationDataUrl =
    unifiedUnit && locationId && day && runsToShow
      ? SC_BASE_PATH +
        "/plotPerLocAndUnitReact.php?json&jsonOnly&unit=" +
        unifiedUnit +
        "&location=" +
        locationId +
        "&day=" +
        day +
        "&windUnits=" +
        windUnits +
        "&forecastToShow=harm" +
        activityUrlString +
        duringDayOnlyString +
        runsString
      : null;

  const graphLocationData = useRemoteData<any>(locationDataUrl, [
    locationId,
    unifiedUnit,
    windUnits,
    day,
    activityUrlString,
    duringDayOnlyString,
    runsToShow,
  ]);

  useEffect(() => {
    if (!graphLocationData || (showSpotHarmonie && !spotHarmData)) {
      setGraphData(null);
      return;
    }
    setGraphData(
      mergeLocationAndSpotData(
        graphLocationData,
        showSpotHarmonie ? spotHarmData[unifiedUnit] : null,
      ),
    );
  }, [graphLocationData, spotHarmData, showSpotHarmonie, unifiedUnit]);

  let options;

  switch (unifiedUnit) {
    case "deg":
      options = clone(optionsWindDir);
      options.plugins.legend.labels.generateLabels = strip_empty_label_plugin;
      options.scales.y.title.text = "Windrichting (graden)";
      options.plugins.title.text =
        "Windrichting (graden) " + locationLbl + " " + day;
      options.scales.y.suggestedMin = 0;
      options.scales.y.suggestedMax = 360;
      options.scales.y.ticks.stepSize = 45;
      options.plugins.tooltip.callbacks.label = labelCallback("graden");

      break;
    case "m/s":
      options = clone(optionsWindSpd);
      const friendlyUnit = windUnits ? friendlyWindUnitName(windUnits) : "";
      options.plugins.legend.labels.generateLabels = strip_empty_label_plugin;
      options.scales.y.suggestedMin = 0;
      options.scales.y.title.text = "Windsnelheid (" + friendlyUnit + ")";
      options.plugins.title.text =
        "Windsnelheid (" + friendlyUnit + ") " + locationLbl + " " + day;
      options.plugins.tooltip.callbacks.label = labelCallback(friendlyUnit);

      break;
    case "C":
      options = clone(optionsWindDir); //TODO heeft deze een eigen nodig?
      options.plugins.legend.labels.generateLabels = strip_empty_label_plugin;
      options.scales.y.title.text = "Temperatuur (Graden C.)";
      options.scales.y.suggestedMin = 0;
      options.scales.y.suggestedMax = 30;
      options.scales.y.ticks.stepSize = 10;
      options.scales.y.ticks = {
        stepSize: 5,
      };
      options.plugins.title.text =
        "Temperatuur (Graden C.) " + locationLbl + " " + day;
      options.plugins.tooltip.callbacks.label = labelCallback(" C.");

      break;
    case "hPa":
      options = clone(optionsWindDir); //TODO heeft deze een eigen nodig?
      options.plugins.legend.labels.generateLabels = strip_empty_label_plugin;
      options.plugins.title.text =
        "Luchtdruk (hectopascal) " + locationLbl + " " + day;
      options.scales.y.title.text = "Luchtdruk (hectopascal)";
      //TODO: ticks.minval en maxval mediaan + en min 12
      options.scales.y.suggestedMin = 980;
      options.scales.y.suggestedMax = 1020;
      options.scales.y.ticks.stepSize = 5;
      options.plugins.tooltip.callbacks.label = labelCallback(" hPa");

      break;
    default:
      // By setting options to null, at least it will become apparent that we forgot a unified unit...
      options = null;
      break;
  }

  return (
    <>
      <br />
      {/* Portrait: {portrait ? "yep" : "Nope"}
      <br />
      graphHeight: {graphHeight}
      <br />
      windowSize:{windowSize.innerWidth + "x" + windowSize.innerHeight}
     */}
      <div style={{ height: graphHeight(windowSize), position: "relative" }}>
        {graphData != null && graphData.data != null ? (
          <>
            <Scatter options={options} data={graphData.data} />
          </>
        ) : (
          "Loading graph data"
        )}
      </div>
    </>
  );
};
